// Libs
import ReceiptIcon from "@mui/icons-material/Receipt";

// Local
import { ExistingPurchaseOrder } from "../../models/purchase-order";
import PillCount from "../PillCount";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import BaseButtonSecondary from "../BaseButtonSecondary";

interface Props {
  purchaseOrders: ExistingPurchaseOrder[];
  onCreatePurchaseOrder: () => void;
  onViewPurchaseOrder: (purchaseOrderId: string) => void;
}

export default function PurchaseOrdersPanel(props: Props): JSX.Element {
  const countPurchaseOrders = props.purchaseOrders.length;

  return (
    <div className="w-full">
      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-2xl">Purchase Orders</h2>
        <BaseButtonSecondary
          type="button"
          onClick={props.onCreatePurchaseOrder}
          className="col-span-2 w-fit justify-self-end uppercase tracking-wider text-primary"
        >
          <ReceiptIcon fontSize="small" className="mr-2" />
          Create Purchase Order
        </BaseButtonSecondary>
      </div>
      <hr className="my-2 block w-full border border-gray-100" />

      <PillCount
        text="Purchase Orders"
        count={countPurchaseOrders}
        color="blue"
        margin="ml-auto mr-1 my-1"
      />
      <hr className="mb-6 mt-2 block w-full border border-gray-100" />

      {countPurchaseOrders < 1 ? (
        <p className="text-sm text-gray-500">No purchase orders for this job</p>
      ) : (
        <div className="space-y-3">
          {props.purchaseOrders.map((po) => (
            <div
              key={po.id}
              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
              onClick={() => props.onViewPurchaseOrder(po.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ReceiptIcon className="h-5 w-5 text-blue-600" />
                  <div>
                    <h3 className="font-medium text-gray-900">
                      PO #{po.poNumber}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {convertToReadableTimestamp(po.issueDate)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">
                    ${po.totalAmount.toFixed(2)}
                  </p>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      po.status === "draft"
                        ? "bg-gray-100 text-gray-800"
                        : po.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : po.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : po.status === "sent"
                              ? "bg-blue-100 text-blue-800"
                              : po.status === "partiallyReceived"
                                ? "bg-orange-100 text-orange-800"
                                : po.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : po.status === "canceled"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {po.status.replace("_", " ").toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Line Items Summary */}
              {po.lineItems && po.lineItems.length > 0 && (
                <div className="mt-3 border-t pt-3">
                  <h4 className="mb-2 text-sm font-medium text-gray-700">
                    Items ({po.lineItems.length}):
                  </h4>
                  <div className="space-y-1">
                    {po.lineItems.slice(0, 3).map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="mr-2 flex-1 truncate text-gray-600">
                          {item.quantity}x {item.title}
                        </span>
                        <span className="font-medium text-gray-900">
                          ${item.subTotal.toFixed(2)}
                        </span>
                      </div>
                    ))}
                    {po.lineItems.length > 3 && (
                      <div className="text-sm italic text-gray-500">
                        +{po.lineItems.length - 3} more items...
                      </div>
                    )}
                  </div>
                </div>
              )}

              {po.notes && (
                <p className="mt-2 text-sm text-gray-600">{po.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
